import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DonationHeader } from './DonationHeader';
import { useLocale } from '@/context/LocaleContext';
import '@/styles/donation-layout.css';
import { useDonationStatus } from '@/hooks/useDonation';
import { api } from '@/lib/axios';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';

export function DonationSuccessPage() {
  const queryClient = useQueryClient();
  const { t } = useLocale();
  const [params] = useSearchParams();
  const orderId = params.get('token');
  const rawId = Number(params.get('donation_id'));
  const [confirmedId, setConfirmedId] = useState<number | null>(null);
  const [confirmationError, setConfirmationError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const fallbackError = t('don_success_fail_msg');
  const donationId = orderId ? confirmedId : Number.isSafeInteger(rawId) && rawId > 0 ? rawId : null;

  useEffect(() => {
    if (!orderId) return;
    let active = true;
    setConfirmationError('');
    setConfirmedId(null);
    api.post<{ data: { donation_id: number } }>('/api/v1/donations/paypal/confirm',
      { order_id: orderId }, { timeout: 45000 })
      .then(({ data }) => { if (active) setConfirmedId(data.data.donation_id); })
      .catch((error: unknown) => {
        if (active) setConfirmationError(
          isAxiosError(error) && typeof error.response?.data?.message === 'string'
            ? error.response.data.message : fallbackError,
        );
      });
    return () => { active = false; };
  }, [orderId, attempt, fallbackError]);

  const { data, isLoading, isError, refetch } = useDonationStatus(donationId, true);
  const error = confirmationError || (isError || (!orderId && !donationId) ? fallbackError : '');
  const status = data?.status ?? 'pending';
  useEffect(() => {
    if (status === 'completed') void queryClient.invalidateQueries({ queryKey: ['donations', 'limit'] });
  }, [status, queryClient]);
  const state = error || status === 'failed' || status === 'cancelled' ? 'error' : status === 'completed' ? 'success' : 'pending';

  return (
    <div className="container-ingame donation-page">
      <div className="donation-container">
        <DonationHeader />
        <div className="donation-result-stage">
        <div className={`donation-state-message donation-result donation-result--${state}`} role="status" aria-live="polite">
          <div className="donation-result-emblem" aria-hidden="true">
            <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {state === 'success' ? <path className="donation-result-check" d="M13 24l8 8 15-17" /> : state === 'error' ? <path d="M24 13v14m0 8h.01" /> : <path d="M24 12a12 12 0 1 1-12 12" />}
            </svg>
          </div>
          {!error && (isLoading || status === 'pending') ? (
            <>
              <h2>{t('don_success_processing')}</h2>
              <p>{t('don_success_wait')}</p>
            </>
          ) : null}

          {!error && status === 'completed' ? (
            <>
              <h2>{t('don_success_ok')}</h2>
              <div className="donation-result-amount" aria-hidden="true">{data?.donation.silks_amount.toLocaleString()}</div>
              <p>
                {t('don_credited', {
                  amount: data?.donation.silks_amount.toLocaleString() ?? '0',
                })}
              </p>
            </>
          ) : null}

          {error || status === 'failed' || status === 'cancelled' ? (
            <>
              <h2>{t('don_success_fail')}</h2>
              <p>{error || t('don_success_fail_msg')}</p>
              {error && (orderId || donationId) ? (
                <button className="donation-btn donation-btn-primary" onClick={() => {
                  if (confirmationError) setAttempt(value => value + 1);
                  else void refetch();
                }}>{t('reg_retry')}</button>
              ) : null}
            </>
          ) : null}

          <Link
            to="/donation"
            className="donation-btn donation-btn-primary"
          >
            {t('don_back_to')}
          </Link>
        </div>
        </div>
      </div>
    </div>
  );
}
